import { Box } from '@chakra-ui/react';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { MODULE_KEYS_METRIC_CARDS_DEER, REVIEWS_METRIC_CARDS } from 'Deer/metricCards/constants';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { useGetReviewsManageTabularDataQueryV1 } from '../../dataHooks';

export const getCardIdFromStatType = (type) => {
  switch (type) {
    case REVIEW_PROVIDER_TYPES.SELF:
      return REVIEWS_METRIC_CARDS?.pendingSelfReviews;
    case REVIEW_PROVIDER_TYPES.MANAGER:
      return REVIEWS_METRIC_CARDS?.pendingManagerReviews;
    case REVIEW_PROVIDER_TYPES.PEER:
      return REVIEWS_METRIC_CARDS?.pendingPeerReviews;
    case REVIEW_PROVIDER_TYPES.UPWARD:
      return REVIEWS_METRIC_CARDS?.pendingUpwardReviews;
    case REVIEW_PROVIDER_TYPES.DLM:
      return REVIEWS_METRIC_CARDS?.pendingDLMReviews;
    default:
      return '';
  }
};

function ReviewManageSummaryIntent({ gridStatePersistenceKey }) {
  const { isFetching, data: reviewManageGridData } = useGetReviewsManageTabularDataQueryV1();

  const reviewManageGridRowData = reviewManageGridData?.rows || [];

  const pendingSelfReviews = reviewManageGridRowData.filter(
    (row) => row.SELF_REVIEW_STATUS && row.SELF_REVIEW_STATUS !== 'SUBMITTED'
  ).length;
  const totalSelfReviews = reviewManageGridRowData.filter((row) => row.SELF_REVIEW_STATUS).length;

  const pendingManagerReviews = reviewManageGridRowData.filter(
    (row) => row.MANAGER_REVIEW_STATUS && row.MANAGER_REVIEW_STATUS !== 'SUBMITTED'
  ).length;
  const totalManagerReviews = reviewManageGridRowData.filter((row) => row.MANAGER_REVIEW_STATUS).length;

  const completedPeerReviews = reviewManageGridRowData
    .filter((row) => row?.PEER_REVIEW_STATUS)
    .reduce((acc, cur) => acc + cur.PEER_REVIEW_STATUS.completed, 0);

  const totalPeerReviews = reviewManageGridRowData
    .filter((row) => row.PEER_REVIEW_STATUS)
    .reduce((acc, cur) => acc + cur.PEER_REVIEW_STATUS.total, 0);

  const pendingPeerReviews = totalPeerReviews - completedPeerReviews;

  const completeUpwardReviews = reviewManageGridRowData
    .filter((row) => row?.UPWARD_REVIEW_STATUS)
    .reduce((acc, cur) => acc + cur.UPWARD_REVIEW_STATUS.completed, 0);

  const totalUpwardReviews = reviewManageGridRowData
    .filter((row) => row.UPWARD_REVIEW_STATUS)
    .reduce((acc, cur) => acc + cur.UPWARD_REVIEW_STATUS.total, 0);

  const pendingUpwardReviews = totalUpwardReviews - completeUpwardReviews;

  const completeDLMReviews = reviewManageGridRowData
    .filter((row) => row?.DLM_REVIEW_STATUS)
    .reduce((acc, cur) => acc + cur.DLM_REVIEW_STATUS.completed, 0);

  const totalDLMReviews = reviewManageGridRowData
    .filter((row) => row.DLM_REVIEW_STATUS)
    .reduce((acc, cur) => acc + cur.DLM_REVIEW_STATUS.total, 0);

  const pendingDLMReviews = totalDLMReviews - completeDLMReviews;

  const totalSelfNumericRatings = reviewManageGridRowData.filter(
    (row) => row?.OVERALL_RATING && row?.SELF_OVERALL_RATING?.ratingType === 'NUMBER_RATING'
  );

  const averageSelfRating = totalSelfNumericRatings.length
    ? (
        totalSelfNumericRatings.reduce((acc, cur) => acc + Number(cur?.SELF_OVERALL_RATING?.ratingText), 0) /
        totalSelfNumericRatings.length
      ).toFixed(2)
    : null;

  const totalManagerNumericRatings = reviewManageGridRowData.filter(
    (row) => row?.OVERALL_RATING && row?.MANAGER_OVERALL_RATING?.ratingType === 'NUMBER_RATING'
  );

  const averageManagerRating = totalManagerNumericRatings.length
    ? (
        totalManagerNumericRatings.reduce((acc, cur) => acc + Number(cur?.MANAGER_OVERALL_RATING?.ratingText), 0) /
        totalManagerNumericRatings.length
      ).toFixed(2)
    : null;

  const totalPeerNumericRatings = reviewManageGridRowData.filter(
    (row) => row?.OVERALL_RATING && row?.PEER_OVERALL_RATING?.ratingType === 'NUMBER_RATING'
  );

  const averagePeerRating = totalPeerNumericRatings.length
    ? (
        totalPeerNumericRatings.reduce((acc, cur) => acc + Number(cur?.PEER_OVERALL_RATING?.ratingText), 0) /
        totalPeerNumericRatings.length
      ).toFixed(2)
    : null;

  const totalUpwardNumericRatings = reviewManageGridRowData.filter(
    (row) => row?.OVERALL_RATING && row?.UPWARD_OVERALL_RATING?.ratingType === 'NUMBER_RATING'
  );

  const averageUpwardRating = totalUpwardNumericRatings.length
    ? (
        totalUpwardNumericRatings.reduce((acc, cur) => acc + Number(cur?.UPWARD_OVERALL_RATING?.ratingText), 0) /
        totalUpwardNumericRatings.length
      ).toFixed(2)
    : null;

  const totalDLMNumericRatings = reviewManageGridRowData.filter(
    (row) => row?.OVERALL_RATING && row?.DLM_OVERALL_RATING?.ratingType === 'NUMBER_RATING'
  );

  const averageDLMRating = totalDLMNumericRatings.length
    ? (
        totalDLMNumericRatings.reduce((acc, cur) => acc + Number(cur?.DLM_OVERALL_RATING?.ratingText), 0) /
        totalDLMNumericRatings.length
      ).toFixed(2)
    : null;

  const cardKPIStats = {
    SELF: { averageRating: averageSelfRating, pendingReviews: pendingSelfReviews, totalReviews: totalSelfReviews },
    PEER: { averageRating: averagePeerRating, pendingReviews: pendingPeerReviews, totalReviews: totalPeerReviews },
    UPWARD: {
      averageRating: averageUpwardRating,
      pendingReviews: pendingUpwardReviews,
      totalReviews: totalUpwardReviews,
    },
    DLM: { averageRating: averageDLMRating, pendingReviews: pendingDLMReviews, totalReviews: totalDLMReviews },
    MANAGER: {
      averageRating: averageManagerRating,
      pendingReviews: pendingManagerReviews,
      totalReviews: totalManagerReviews,
    },
  };

  // Remove the card if there are no reviews
  Object.keys(cardKPIStats).forEach((key) => {
    if (cardKPIStats[key].totalReviews === 0) {
      delete cardKPIStats[key];
    }
  });

  function getStatTitle(type) {
    let text = '';

    // eslint-disable-next-line default-case
    switch (type) {
      case REVIEW_PROVIDER_TYPES.SELF:
        text = 'Self Reviews';
        break;
      case REVIEW_PROVIDER_TYPES.MANAGER:
        text = 'Manager Reviews';
        break;
      case REVIEW_PROVIDER_TYPES.PEER:
        text = 'Peer Reviews';
        break;
      case REVIEW_PROVIDER_TYPES.UPWARD:
        text = 'Upward Reviews';
        break;
      case REVIEW_PROVIDER_TYPES.DLM:
        text = 'DLM Reviews';
    }

    return `Pending ${text}`;
  }

  if (isFetching) {
    return <SkeletonLoader />;
  }

  const cardKPIData = Object.keys(cardKPIStats ?? []).map((type) => ({
    cardId: getCardIdFromStatType(type),
    title: getStatTitle(type),
    count: cardKPIStats?.[type]?.pendingReviews || '0',
    subTitle: ![undefined, null].includes(cardKPIStats?.[type]?.averageRating)
      ? `Average score : ${cardKPIStats?.[type]?.averageRating}`
      : '',
    totalCount: cardKPIStats?.[type]?.totalReviews,
  }));

  if (!cardKPIData?.length) {
    return null;
  }

  return (
    <Box mb={3}>
      <MetricCards
        height="100%"
        columns={`repeat(${cardKPIData?.length}, 1fr)`}
        cardTitle="Metrics"
        cards={cardKPIData}
        module={MODULE_KEYS_METRIC_CARDS_DEER.REVIEW}
        gridStatePersistenceKey={gridStatePersistenceKey}
        cardStyle="style2"
      />
    </Box>
  );
}

export default ReviewManageSummaryIntent;
